/** Pure helpers for the Minecart Switchyard play screen (what a miss says and where it points). */
import type { Cargo, CartRun, Failure, MinecartLevel } from './minecart.ts';

export const CARGO_NAME: Record<Cargo, string> = {
  redstone: 'Redstone',
  lapis: 'Lapis',
  emerald: 'Emerald',
};

/** "Lapis cart", or "Redstone cart 2" when another cart carries the same cargo. */
export function cartLabel(level: MinecartLevel, i: number): string {
  const cargo = level.carts[i]!.cargo;
  const same = level.carts.filter((c) => c.cargo === cargo).length > 1;
  return same ? `${CARGO_NAME[cargo]} cart ${i + 1}` : `${CARGO_NAME[cargo]} cart`;
}

/** The heading and one kid sentence for a failed run (no ticks, rows or columns). */
export function failText(level: MinecartLevel, f: Failure): { title: string; say: string } {
  const the = (i: number) => {
    const s = cartLabel(level, i);
    return /\d$/.test(s) ? s : `the ${s}`;
  };
  const The = (i: number) => the(i).replace(/^t/, 'T');
  switch (f.kind) {
    case 'crash':
      return { title: 'Crash!', say: `${The(f.carts[0])} and ${the(f.carts[1])} hit each other.` };
    case 'swap':
      return { title: 'Crash!', say: `${The(f.carts[0])} and ${the(f.carts[1])} met head-on.` };
    case 'spawn':
      return {
        title: 'Crash!',
        say: `${The(f.carts[1])} could not start: ${the(f.carts[0])} was rolling onto its spot.`,
      };
    case 'dead-end':
      return { title: 'Off the rails', say: `${The(f.cart)} ran off the end of the rails.` };
    case 'wrong-depot':
      return {
        title: 'Wrong depot',
        say: `${The(f.cart)} went to the ${CARGO_NAME[f.depot]} depot.`,
      };
    case 'too-long':
      return {
        title: 'Round and round',
        say: 'A cart kept going round and round and never got to a depot.',
      };
  }
}

export interface FailSpot {
  x: number;
  y: number;
  /** The callout on the marked square. */
  text: string;
}

/** The square a miss marks on the yard, or null (a win, or a cart that never stops). */
export function failSpot(run: CartRun): FailSpot | null {
  const f = run.outcome;
  if (f === 'win') return null;
  const last = run.frames.at(-1)!;
  switch (f.kind) {
    case 'crash':
    case 'spawn':
      return { x: f.x, y: f.y, text: 'Crash here' };
    case 'swap':
      return { x: last[f.carts[0]]!.x, y: last[f.carts[0]]!.y, text: 'Head-on crash' };
    // The engine reports the square past the end, which can be outside the yard: mark the last rail.
    case 'dead-end':
      return { x: last[f.cart]!.x, y: last[f.cart]!.y, text: 'Rails end here' };
    case 'wrong-depot':
      return { x: last[f.cart]!.x, y: last[f.cart]!.y, text: 'Wrong depot' };
    case 'too-long':
      return null;
  }
}
